import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Eye, Phone } from "lucide-react";

interface SwapRequest {
  id: string;
  customer_id: string;
  current_device_brand: string;
  current_device_model: string;
  storage_capacity: string;
  battery_health: string;
  device_condition: string;
  additional_notes: string | null;
  status: string;
  vendor_offer_amount: number | null;
  vendor_offer_notes: string | null;
  created_at: string;
  front_image_url: string;
  back_image_url: string;
  additional_images: string[];
  target_menu_item: {
    name: string;
    price: number;
  };
  customer_name?: string;
  customer_phone?: string;
}

interface VendorSwapRequestsProps {
  restaurantId: string;
}

export const VendorSwapRequests = ({ restaurantId }: VendorSwapRequestsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [requests, setRequests] = useState<SwapRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<SwapRequest | null>(null);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [imagesDialogOpen, setImagesDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [offerAmount, setOfferAmount] = useState("");
  const [offerNotes, setOfferNotes] = useState("");

  useEffect(() => {
    if (restaurantId) {
      fetchSwapRequests();
    }
  }, [restaurantId]);

  const fetchSwapRequests = async () => {
    try {
      const { data, error } = await supabase
        .from("swap_requests")
        .select(`
          *,
          target_menu_item:menu_items!swap_requests_target_menu_item_id_fkey(name, price)
        `)
        .eq("restaurant_id", restaurantId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch customer info separately
      if (data) {
        const customerIds = [...new Set(data.map(r => r.customer_id))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name, phone")
          .in("id", customerIds);

        const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

        const enrichedRequests = data.map(request => ({
          ...request,
          customer_name: profileMap.get(request.customer_id)?.full_name || "Unknown",
          customer_phone: profileMap.get(request.customer_id)?.phone || "",
        }));

        setRequests(enrichedRequests);
      }
    } catch (error) {
      console.error("Error fetching swap requests:", error);
      toast({
        title: "Error",
        description: "Failed to load swap requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMakeOffer = (request: SwapRequest) => {
    setSelectedRequest(request);
    setOfferAmount(request.vendor_offer_amount?.toString() || "");
    setOfferNotes(request.vendor_offer_notes || "");
    setOfferDialogOpen(true);
  };

  const handleSubmitOffer = async () => {
    if (!selectedRequest || !offerAmount) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from("swap_requests")
        .update({
          status: "offer_made",
          vendor_offer_amount: parseFloat(offerAmount),
          vendor_offer_notes: offerNotes,
          offer_made_at: new Date().toISOString(),
        })
        .eq("id", selectedRequest.id);

      if (error) throw error;

      toast({
        title: "Offer sent",
        description: "The customer will be notified of your offer",
      });

      setOfferDialogOpen(false);
      await fetchSwapRequests();
    } catch (error) {
      console.error("Error submitting offer:", error);
      toast({
        title: "Error",
        description: "Failed to submit offer",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewImages = (request: SwapRequest) => {
    setSelectedRequest(request);
    setImagesDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      pending: { label: "New", className: "bg-yellow-100 text-yellow-800" },
      offer_made: { label: "Offer Made", className: "bg-blue-100 text-blue-800" },
      accepted: { label: "Accepted", className: "bg-green-100 text-green-800" },
      declined: { label: "Declined", className: "bg-red-100 text-red-800" },
      completed: { label: "Completed", className: "bg-gray-100 text-gray-800" },
    };

    const variant = variants[status] || variants.pending;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {requests.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-600">No swap requests yet</p>
          </CardContent>
        </Card>
      ) : (
        requests.map((request) => (
          <Card key={request.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">
                    {request.current_device_brand} {request.current_device_model}
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    Customer: {request.customer_name}
                  </p>
                  {request.customer_phone && (
                    <a 
                      href={`tel:${request.customer_phone}`}
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      <Phone className="h-3 w-3" />
                      {request.customer_phone}
                    </a>
                  )}
                  <p className="text-xs text-gray-500">
                    {new Date(request.created_at).toLocaleDateString()}
                  </p>
                </div>
                {getStatusBadge(request.status)}
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Storage</p>
                  <p className="text-sm">{request.storage_capacity}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Battery Health</p>
                  <p className="text-sm">{request.battery_health}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Condition</p>
                  <p className="text-sm">{request.device_condition}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Wants to swap to</p>
                  <p className="text-sm font-medium">{request.target_menu_item.name}</p>
                </div>
              </div>

              {request.additional_notes && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Additional Notes</p>
                  <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{request.additional_notes}</p>
                </div>
              )}

              {request.vendor_offer_amount && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-1">Your Offer</p>
                  <p className="text-xl font-bold text-blue-600">D{request.vendor_offer_amount}</p>
                  {request.vendor_offer_notes && (
                    <p className="text-sm text-gray-600 mt-1">{request.vendor_offer_notes}</p>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewImages(request)}
                  className="flex-1"
                >
                  <Eye className="mr-2 h-4 w-4" />
                  View Photos
                </Button>
                {(request.status === "pending" || request.status === "offer_made") && (
                  <Button
                    size="sm"
                    onClick={() => handleMakeOffer(request)}
                    className="flex-1"
                  >
                    {request.status === "offer_made" ? "Update Offer" : "Make Offer"}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Make Offer Dialog */}
      <Dialog open={offerDialogOpen} onOpenChange={setOfferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Make Offer</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Offer Amount (GMD) *</Label>
              <Input
                id="amount"
                type="number"
                value={offerAmount}
                onChange={(e) => setOfferAmount(e.target.value)}
                placeholder="Enter offer amount"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={offerNotes}
                onChange={(e) => setOfferNotes(e.target.value)}
                placeholder="Add any notes about the offer..."
                rows={3}
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setOfferDialogOpen(false)}
                className="flex-1"
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitOffer}
                className="flex-1"
                disabled={submitting || !offerAmount}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Send Offer"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Images Dialog */}
      <Dialog open={imagesDialogOpen} onOpenChange={setImagesDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Device Photos</DialogTitle>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Front</h4>
                <img
                  src={selectedRequest.front_image_url}
                  alt="Front"
                  className="w-full rounded-lg"
                />
              </div>

              <div>
                <h4 className="font-semibold mb-2">Back</h4>
                <img
                  src={selectedRequest.back_image_url}
                  alt="Back"
                  className="w-full rounded-lg"
                />
              </div>

              {selectedRequest.additional_images.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Additional Photos</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedRequest.additional_images.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`Additional ${idx + 1}`}
                        className="w-full rounded-lg"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};